import { useState } from 'react';
import axios from 'axios';
import { X, Plus, Calendar, Download } from 'lucide-react';
import { format } from 'date-fns';
import { downloadICS, googleCalendarLink } from './calendarExport';

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

export default function SessionModal({
  mode, initialDate, session,
  sessionTypes, agendaStudents,
  token, backendUrl,
  onSaved, onClose, onSessionTypesChange,
}) {
  const isEdit = mode === 'edit';
  const dateStr = initialDate instanceof Date
    ? format(initialDate, 'yyyy-MM-dd')
    : (initialDate || format(new Date(), 'yyyy-MM-dd'));

  const [form, setForm] = useState({
    typeId: session?.typeId || '',
    title: session?.title || '',
    date: isEdit ? session?.date : dateStr,
    time: session?.time || '07:00',
    durationMin: session?.durationMin || 60,
    studentIds: session?.studentIds || [],
    notes: session?.notes || '',
    recurrenceEnabled: !!(session?.recurrence?.type),
    recurrenceDays: session?.recurrence?.days || [],
    recurrenceEndDate: session?.recurrence?.endDate || '',
  });
  const [newTypeName, setNewTypeName] = useState('');
  const [showNewType, setShowNewType] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const selectedType = sessionTypes.find(t => t.id === form.typeId);
  const selectedStudents = agendaStudents.filter(s => form.studentIds.includes(s.id));

  const handleCreateType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const res = await axios.post(`${backendUrl}/api/admin/agenda/session-types`,
        { name: newTypeName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await onSessionTypesChange?.();
      setForm(f => ({ ...f, typeId: res.data.id }));
      setNewTypeName('');
      setShowNewType(false);
    } catch { setError('Erro ao criar tipo.'); }
  };

  const toggleStudent = (id) => {
    setForm(f => ({
      ...f,
      studentIds: f.studentIds.includes(id)
        ? f.studentIds.filter(s => s !== id)
        : [...f.studentIds, id],
    }));
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      recurrenceDays: f.recurrenceDays.includes(day)
        ? f.recurrenceDays.filter(d => d !== day)
        : [...f.recurrenceDays, day],
    }));
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.typeId) errors.typeId = true;
    if (!form.date) errors.date = true;
    if (!form.time) errors.time = true;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Preencha os campos obrigatórios indicados em vermelho.');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    setError('');
    const payload = {
      typeId: form.typeId,
      title: form.title,
      date: form.date,
      time: form.time,
      durationMin: Number(form.durationMin),
      studentIds: form.studentIds,
      notes: form.notes,
      recurrence: form.recurrenceEnabled && form.recurrenceDays.length > 0
        ? { type: 'weekly', days: form.recurrenceDays, endDate: form.recurrenceEndDate }
        : null,
    };
    try {
      if (isEdit) {
        await axios.patch(`${backendUrl}/api/admin/agenda/sessions/${session.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${backendUrl}/api/admin/agenda/sessions`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar sessão.');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${backendUrl}/api/admin/agenda/sessions/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved?.();
    } catch { setError('Erro ao excluir.'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-sm font-bold uppercase tracking-widest">
            {isEdit ? 'Editar Sessão' : 'Nova Sessão'}
          </h2>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-6 flex flex-col gap-5">
          {error && <p className="text-red-400 text-xs">{error}</p>}

          {/* Tipo */}
          <div>
            <label className="block text-xs uppercase tracking-widest mb-2 flex items-center gap-1">
              <span className={fieldErrors.typeId ? 'text-red-400' : 'text-[#A3A3A3]'}>Tipo</span>
              <span className="text-red-400">*</span>
            </label>
            {!showNewType ? (
              <div className="flex gap-2">
                <select
                  value={form.typeId}
                  onChange={e => { setForm(f => ({ ...f, typeId: e.target.value })); setFieldErrors(f => ({ ...f, typeId: false })); }}
                  className={`flex-1 px-3 py-2 bg-[#0A0A0A] outline-none text-sm text-white border transition-colors ${
                    fieldErrors.typeId ? 'border-red-500/60 focus:border-red-400' : 'border-white/10 focus:border-white/30'
                  }`}
                >
                  <option value="">Selecione o tipo...</option>
                  {sessionTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <button onClick={() => setShowNewType(true)}
                  className="px-3 py-2 border border-white/10 hover:border-white/30 text-[#A3A3A3] hover:text-white text-xs transition-colors"
                  title="Criar novo tipo"
                >
                  <Plus size={13} />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={newTypeName}
                  onChange={e => setNewTypeName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateType()}
                  onBlur={() => { if (newTypeName.trim()) handleCreateType(); }}
                  placeholder="Nome do tipo — pressione Enter para criar"
                  className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm"
                />
                <button onClick={handleCreateType}
                  className="px-3 py-2 bg-white text-black text-xs font-bold hover:bg-[#E5E5E5] transition-colors"
                >
                  Criar
                </button>
                <button onClick={() => { setShowNewType(false); setNewTypeName(''); }}
                  className="px-2 py-2 border border-white/10 text-[#A3A3A3] hover:text-white"
                >
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Data / Hora / Duração */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2 flex items-center gap-1">
                <span className={fieldErrors.date ? 'text-red-400' : 'text-[#A3A3A3]'}>Data</span>
                <span className="text-red-400">*</span>
              </label>
              <input type="date" value={form.date}
                onChange={e => { setForm(f => ({ ...f, date: e.target.value })); setFieldErrors(f => ({ ...f, date: false })); }}
                className={`w-full px-3 py-2 bg-[#0A0A0A] outline-none text-sm border transition-colors ${
                  fieldErrors.date ? 'border-red-500/60 focus:border-red-400' : 'border-white/10 focus:border-white/30'
                }`}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2 flex items-center gap-1">
                <span className={fieldErrors.time ? 'text-red-400' : 'text-[#A3A3A3]'}>Hora</span>
                <span className="text-red-400">*</span>
              </label>
              <input type="time" value={form.time}
                onChange={e => { setForm(f => ({ ...f, time: e.target.value })); setFieldErrors(f => ({ ...f, time: false })); }}
                className={`w-full px-3 py-2 bg-[#0A0A0A] outline-none text-sm border transition-colors ${
                  fieldErrors.time ? 'border-red-500/60 focus:border-red-400' : 'border-white/10 focus:border-white/30'
                }`}
              />
            </div>
            <div>
              <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Duração (min)</label>
              <input type="number" min="15" step="15" value={form.durationMin}
                onChange={e => setForm(f => ({ ...f, durationMin: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm"
              />
            </div>
          </div>

          {/* Alunos */}
          <div>
            <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Alunos</label>
            <div className="flex flex-wrap gap-2">
              {agendaStudents.filter(s => s.active).map(s => (
                <button key={s.id} onClick={() => toggleStudent(s.id)}
                  className={`px-3 py-1.5 text-xs border transition-colors ${
                    form.studentIds.includes(s.id)
                      ? 'border-white text-white bg-white/10'
                      : 'border-white/10 text-[#A3A3A3] hover:border-white/30'
                  }`}
                >
                  {s.name.split(' ')[0]}
                </button>
              ))}
              {agendaStudents.filter(s => s.active).length === 0 && (
                <p className="text-xs text-[#555]">Nenhum aluno cadastrado ainda.</p>
              )}
            </div>
          </div>

          {/* Recorrência */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <label className="text-xs text-[#A3A3A3] uppercase tracking-widest">Recorrência semanal</label>
              <button onClick={() => setForm(f => ({ ...f, recurrenceEnabled: !f.recurrenceEnabled }))}
                className={`relative w-10 h-5 transition-colors ${form.recurrenceEnabled ? 'bg-white' : 'bg-white/10'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-black transition-transform ${form.recurrenceEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {form.recurrenceEnabled && (
              <div className="flex flex-col gap-3 pl-2">
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map(day => (
                    <button key={day} onClick={() => toggleDay(day)}
                      className={`px-2.5 py-1 text-[11px] border transition-colors ${
                        form.recurrenceDays.includes(day)
                          ? 'border-white text-white bg-white/10'
                          : 'border-white/10 text-[#555] hover:border-white/20'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-xs text-[#555] mb-1">Data de encerramento</label>
                  <input type="date" value={form.recurrenceEndDate}
                    onChange={e => setForm(f => ({ ...f, recurrenceEndDate: e.target.value }))}
                    className="px-3 py-1.5 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Notas (opcional)</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm resize-none"
            />
          </div>

          {/* Adicionar ao calendário */}
          {form.date && form.time && form.typeId && (
            <div>
              <button onClick={() => setShowExport(!showExport)}
                className="flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white transition-colors"
              >
                <Calendar size={13} /> Adicionar ao calendário
              </button>
              {showExport && (
                <div className="flex gap-2 mt-2 pl-5">
                  <button
                    onClick={() => downloadICS(
                      { ...form, id: session?.id, durationMin: Number(form.durationMin) },
                      selectedType,
                      selectedStudents,
                      form.date
                    )}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white transition-colors"
                  >
                    <Download size={11} /> Baixar .ics
                  </button>
                  <a
                    href={googleCalendarLink(
                      { ...form, durationMin: Number(form.durationMin) },
                      selectedType,
                      selectedStudents,
                      form.date
                    )}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white transition-colors"
                  >
                    Google Calendar
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10">
          {confirmDelete ? (
            <div>
              <p className="text-xs text-[#A3A3A3] mb-3">
                Excluir esta sessão?{' '}
                <span className="text-[#555]">As presenças associadas também serão removidas.</span>
              </p>
              <div className="flex gap-2">
                <button onClick={handleDelete}
                  className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-bold uppercase tracking-wider transition-colors"
                >
                  Excluir
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                {isEdit && (
                  <button onClick={() => setConfirmDelete(true)}
                    className="text-[#555] hover:text-red-400 text-xs transition-colors"
                  >
                    Excluir sessão
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={onClose}
                  className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
                >
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
